import "./UserAddComponent.scss";
import {ITimelineItem} from "../../../shared/models/timeline.model";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import { useNavigate, useSearchParams} from "react-router-dom";
import {useDispatch} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {ImageConfig} from "../../../constants";
import Grid from "@mui/material/Grid";
import TimelineComponent from "../../../shared/components/timeline/timelineComponent";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import UserPermissionsComponent from "./user-permissions/UserPermissionsComponent";
import UserDetailsComponent from "./user-details/UserDetailsComponent";

interface UserAddComponentProps {

}

const USER_DETAILS = 'user_information';
const PERMISSIONS = 'permissions';

const STEPS: ITimelineItem[] = [
    {
        id: USER_DETAILS,
        title: "User Details",
    },
    {
        id: PERMISSIONS,
        title: "Permissions",
    },
]

const UserAddComponent = (props: UserAddComponentProps) => {

    const [steps] = useState<ITimelineItem[]>([..._.cloneDeep(STEPS)]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentStep, setCurrentStep] = useState<ITimelineItem | undefined>(STEPS[0]);
    const [userID, setUserID] = useState<number | undefined>(undefined);
    const [activeStep, setActiveStep] = React.useState(0);
    const [userDetails, setUserDetails] = useState<any>(undefined);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setCurrentNavParams("", "User"));
        dispatch(setSideMenuView('compact'));
    }, [dispatch]);

    const [isUserDetailsLoading, setIsUserDetailsLoading] = useState<boolean>(false);

    const updateSteps = useCallback((currentStepIndex: number) => {
        setCurrentStep(STEPS[currentStepIndex]);
        searchParams.set("step", STEPS[currentStepIndex].id);
        setSearchParams(searchParams);
        setActiveStep(currentStepIndex);
    }, [searchParams, setSearchParams])

    const getUserDetails = useCallback(() => {
        let userDetails: any = {};
        setIsUserDetailsLoading(true);
        CommonService._user.getUserDetails(userID, {})
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    userDetails = response.data;
                }
                setUserDetails(userDetails);
                setIsUserDetailsLoading(false);
            })
            .catch((error: any) => {
                setUserDetails(userDetails);
                setIsUserDetailsLoading(false);
            });
    }, [userID]);


    const handlePrevious = useCallback((currentStepIndex: number) => {
        updateSteps(currentStepIndex);
    }, [updateSteps]);

    const handleUserCancel = useCallback(() => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to exit without adding this user?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: "Exit",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        }).then(() => {
            navigate(CommonService._routeConfig.UserListRoute());
        })
            .catch(() => {
            });
    }, [navigate]);


    const handleADDSuccess = useCallback(() => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Congratulations! ',
            confirmationSubTitle: 'User has been successfully created.',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.CongratulationImage}`,
            direction: "up",
            hideNoOption: true,
            yes: {
                text: "Proceed",
                color: "primary"
            }

        }).then(() => {
            const redirectUrl = searchParams.get("returnUrl");
            if (redirectUrl) {
                navigate(redirectUrl);
            } else {
                navigate(CommonService._routeConfig.UserListRoute());
            }
        })
            .catch(() => {
            });
    }, [navigate, searchParams]);


    const handleNext = useCallback((currentStepIndex: number, currentStepId: string, userId?: number, data?: any) => {
        switch (currentStepId) {
            case USER_DETAILS :
                if (userId) {
                    setUserID(userId);
                    searchParams.set("userId", userId.toString());
                    setSearchParams(searchParams);
                }
                setUserDetails(data);
                updateSteps(currentStepIndex);
                break;
            case PERMISSIONS:
                handleADDSuccess();
                break;

            default:
                <></>
        }
    }, [searchParams, setSearchParams, handleADDSuccess, updateSteps]);

    useEffect(() => {
        const step = searchParams.get("step");
        setActiveStep(STEPS.findIndex((stepItem: any) => stepItem.id === step));
        const userId = searchParams.get("userId");
        const stepIDS = STEPS.map(item => item.id);
        let stepIndex = 0;
        if (userId) {
            setUserID(parseInt(userId, 10));
            if (stepIDS.indexOf(step) > -1) {
                stepIndex = stepIDS.indexOf(step);
            } else {
                stepIndex = 1;
            }
        } else {
            stepIndex = 0;
            setActiveStep(0)
        }
        setCurrentStep(STEPS[stepIndex]);
    }, [searchParams]);

    const renderComponent = useCallback(() => {
        if (!userID) {
            if (currentStep?.id === USER_DETAILS) {
                return <UserDetailsComponent
                    next={(userId: number, userDetails: any) => handleNext(1, USER_DETAILS, userId, userDetails)}
                    cancel={handleUserCancel}
                />

            }
        } else {
            switch (currentStep && currentStep.id) {
                case USER_DETAILS:
                    return <UserDetailsComponent
                        userID={userID}
                        userDetails={userDetails}
                        cancel={handleUserCancel}
                        next={(userId: number, userDetails: any) => handleNext(1, USER_DETAILS, userId, userDetails)}
                    />
                case PERMISSIONS:
                    return <UserPermissionsComponent
                        userID={userID}
                        userDetails={userDetails}
                        prev={() => handlePrevious(0)}
                        cancel={handleUserCancel}
                        next={(data: number) => handleNext(2, PERMISSIONS, undefined, data)}
                    />
                default:
                    <></>
            }
        }
    }, [userID, userDetails, currentStep, handleNext, handlePrevious, handleUserCancel]);

    useEffect(() => {
        if (userID) {
            getUserDetails();
        }
    }, [userID, getUserDetails]);


    return (
        <div className={'user-add-component  add-layout-with-timeline screen'}>
            <Grid container spacing={2} className="add-form-timeline-wrapper">
                <Grid item sm={12} md={3} xl={3} className="timeline-layout"
                      sx={{
                          display: {xs: "none", md: "block"}
                      }}>
                    <div className="add-layout-title">Adding User</div>
                    <div className="add-layout-timeline">
                        <TimelineComponent
                            activeStep={activeStep}
                            steps={steps}
                        />
                    </div>
                </Grid>
                <Grid item xs={12} sm={12} md={9} xl={9} className="add-form-content">
                    <div className="add-layout-form-content">
                        {renderComponent()}
                    </div>
                </Grid>
            </Grid>
            {isUserDetailsLoading &&
                <div className={"glass-effect h-v-center flex-direction-column"}>
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
        </div>
    );

};

export default UserAddComponent;
